import Button from '../../button';
import { Buttons, DropData, Form, Icon, Label, ModalBlock, SecModalContent } from './styles';
import { BUTTON_TYPE, LABEL_COLOURS, LABEL_COLOUR_HASH } from '../../../global/constants';
import { useCallback, useMemo, useState } from 'react';
import { createNewLabel, getCurrentProjectDetails, updateLabel } from '../../../services/projectServices';
import isEmpty from 'lodash/isEmpty';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { captureException } from '../../../services/logService';
import { nanoid } from 'nanoid';
import { Dropdown, DropdownItem } from '../../dropdown';
import { dispatchSettingError } from '../../../services/settingServices';
import { Label as LabelInterface } from '../../../mobx/interfaces/project';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import ModalHeader from '../modalHeader';
import Input from '../../input';

interface Props {
  onClose: () => void;
  labelData?: LabelInterface | null;
  isTemplateProject?: boolean;
}
function LabelModal({ onClose, isTemplateProject = false, labelData }: Props) {
  //States
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [labelInput, setLabelInput] = useState<{
    name: string;
    color: string;
  }>({
    name: labelData?.name || '',
    color: labelData?.color || ''
  });
  //Mobx store variables
  const { projectStore, projectTemplateStore } = rootStore;
  const { currentProject } = projectStore;
  const { currentProjectTemplate } = projectTemplateStore;

  const addLabelInTemplateProject = useCallback(
    (labelObj: LabelInterface) => {
      const updatedLabels = [...(currentProjectTemplate?.labelsList || []), labelObj];
      currentProjectTemplate?.setLabelsList(updatedLabels);
    },
    [currentProjectTemplate]
  );

  const updateLabelInTemplateProject = useCallback(
    (labelObj: LabelInterface) => {
      const updatedLabels = currentProjectTemplate?.labelsList?.map((x) => {
        if (x?.id === labelObj?.id) return labelObj;
        else return x;
      });
      currentProjectTemplate?.setLabelsList(updatedLabels || []);
    },
    [currentProjectTemplate]
  );

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      if (isEmpty(labelInput?.name)) {
        dispatchSettingError('Please enter label name.');
        return;
      } else if (isEmpty(labelInput?.color)) {
        dispatchSettingError('Please select label color.');
        return;
      }
      setIsLoading(true);
      try {
        let result;
        if (!isEmpty(labelData?.id)) {
          if (isTemplateProject) {
            const user = UserPreferenceSingleton.getInstance().getCurrentUser();
            const labelObj: LabelInterface = {
              id: labelData?.id || '',
              name: labelInput?.name,
              color: labelInput?.color,
              createdBy: labelData?.createdBy || '',
              createdOn: labelData?.createdOn || '',
              updatedBy: user?.id || '',
              updatedOn: new Date().toISOString()
            };
            updateLabelInTemplateProject(labelObj);
            onClose();
          } else {
            const payload = {
              name: labelInput?.name,
              color: labelInput?.color
            };
            result = await updateLabel(payload, currentProject?.id, labelData?.id);
          }
        } else {
          if (isTemplateProject) {
            const user = UserPreferenceSingleton.getInstance().getCurrentUser();
            const labelObj = {
              id: String(nanoid()),
              name: labelInput?.name,
              color: labelInput?.color,
              createdBy: user?.id || '',
              createdOn: new Date().toISOString(),
              updatedBy: user?.id || '',
              updatedOn: new Date().toISOString()
            };
            addLabelInTemplateProject(labelObj);
            onClose();
          } else {
            const payload = {
              name: labelInput?.name,
              color: labelInput?.color
            };
            result = await createNewLabel(payload, currentProject?.id);
          }
        }
        if (result) {
          onClose();
          await getCurrentProjectDetails(currentProject?.id);
        }
      } catch (error) {
        captureException(error);
      } finally {
        setLabelInput({
          name: '',
          color: ''
        });
        setIsLoading(false);
      }
    },
    [
      addLabelInTemplateProject,
      currentProject?.id,
      isTemplateProject,
      labelData?.createdBy,
      labelData?.createdOn,
      labelData?.id,
      labelInput?.color,
      labelInput?.name,
      onClose,
      updateLabelInTemplateProject
    ]
  );

  const onChangeInput = useCallback((propsName: string, value: string) => {
    setLabelInput((prevState) => ({
      ...prevState,
      [propsName]: value
    }));
  }, []);

  const renderLabelItems = useMemo(() => {
    return LABEL_COLOURS.map((x) => (
      <DropdownItem key={x?.label} onClick={() => onChangeInput('color', x?.label)} label={x?.label}>
        <Icon iconColor={x.color} />
      </DropdownItem>
    ));
  }, [onChangeInput]);

  return (
    <>
      <ModalHeader title={!isEmpty(labelData?.id) ? 'Update Label' : 'Create New Label'} onClose={onClose} />
      <ModalBlock onSubmit={onSubmit}>
        <SecModalContent>
          <Input
            label='Label Name'
            autoFocus={true}
            placeHolder='Enter label name'
            value={labelInput?.name}
            onChange={(e) => onChangeInput('name', e.target.value)}
          />
          <Form>
            <Label>Label Color</Label>
            <Dropdown
              isMinWidth={428}
              updateDropdownOpen={setIsDropdownOpen}
              content={
                <Input
                  labelIconColor={LABEL_COLOUR_HASH[labelInput?.color]}
                  placeHolder='Select color'
                  value={labelInput?.color}
                  trailingIconName='down-arrow-icon'
                  trailingIconClassName={`angleDown-icon ${isDropdownOpen ? 'rotate' : ''}`}
                />
              }
              trigger='click'>
              <DropData>{renderLabelItems}</DropData>
            </Dropdown>
          </Form>
          <Buttons>
            <Button title='Cancel' variant={BUTTON_TYPE.SECONDARY} buttonType='button' onClick={onClose} />
            <Button
              variant={BUTTON_TYPE.PRIMARY}
              buttonType='submit'
              title={labelData?.id ? 'update' : 'Create Label'}
              isLoading={isLoading}
            />
          </Buttons>
        </SecModalContent>
      </ModalBlock>
    </>
  );
}
export default observer(LabelModal);
